import {SocketApi} from "proto_socket_typescript";
import {MladiVoznikStore} from "./mladi_voznik_store";
import {CalendarController} from "../forms/components/calendar";
import {autorun, makeAutoObservable, runInAction} from "mobx";
import {proto} from "../proto/messages";
import {cvv_types, calendar} from "../proto/compiled";
import {FormStore} from "./form_store";
import {toast} from "react-toastify";

export class MladiVoznikStoreS2 {
    subscriptions: any[] = [];
    /** Prevents init() from being called multiple times */
    initialized = false;
    /**
     * When true, regular calendar endpoints (TxLoadCalendar, TxLoadCalendarDay) are used.
     * Set to true when: a) preselected date exists, or b) combined endpoint response received.
     * When false, we're waiting for the combined endpoint response.
     */
    calendarReady = false;
    /** Set once from initial load - true if no termini exist for this course at all */
    noTerminiAvailable = false;
    api: SocketApi;
    base: MladiVoznikStore;
    error?: string;
    numberOfPeople = 1;
    freePlaces?:  number | null | undefined;

    calendarController: CalendarController;
    selectedDay?: Date;
    calendarData?: calendar.ICalendarData;
    selectedDayData?: calendar.ICalendarDayData;

    // Query params for pre-selection from termini page
    private preselectedLectureId?: string;
    private preselectedDate?: Date;

    constructor(base: MladiVoznikStore, api: SocketApi) {
        this.api = api;
        this.base = base;

        // Parse query params for lid and ltd
        const urlParams = new URLSearchParams(window.location.search);
        const lid = urlParams.get('lid');
        const ltd = urlParams.get('ltd');

        if (lid) {
            this.preselectedLectureId = lid;
        }
        if (ltd) {
            const [year, month, day] = ltd.split('-').map(Number);
            if (year && month && day) {
                this.preselectedDate = new Date(year, month - 1, day);
            }
        }

        if (this.base.form.step2?.date?.dateTime?.ts) {
            const date = new Date(this.base.form.step2!.date!.dateTime!.ts as number);
            this.selectedDay = date;
            this.calendarController = new CalendarController(date.getFullYear(), date.getMonth())
            this.calendarReady = true;
        } else if (this.preselectedDate) {
            this.selectedDay = this.preselectedDate;
            this.calendarController = new CalendarController(this.preselectedDate.getFullYear(), this.preselectedDate.getMonth())
            this.calendarReady = true;
        } else {
            const now = new Date();
            this.calendarController = new CalendarController(now.getFullYear(), now.getMonth())
        }

        this.subscriptions.push(api.getMessageHandler(new proto.RxCalendarData()).subscribe(
            (m) => this.onCalendarData(m)
        ));
        this.subscriptions.push(api.getMessageHandler(new proto.RxCalendarDayData()).subscribe(
            (m) => this.onCalendarDayData(m)
        ));
        this.subscriptions.push(api.getMessageHandler(new proto.RxFirstAvailableCalendarData()).subscribe(
            (m) => this.onFirstAvailableCalendarData(m)
        ));
        makeAutoObservable(this);
    }

    // B2B: termin je fiksen in pride iz FormMeta, koledarja sploh ne nalagamo
    get fixedLecture(): calendar.IAvailableLecture | undefined {
        const meta = this.base.formStore?.meta;
        return meta?.b2b ? (meta.fixedLecture ?? undefined) : undefined;
    }

    get fixedLectureFull(): boolean {
        const lecture = this.fixedLecture;
        if (!lecture) return false;
        const past = (lecture.dateTime?.ts as number | undefined) !== undefined
            && (lecture.dateTime!.ts as number) < Date.now();
        return past || (lecture.freePlaces ?? 0) <= 0;
    }

    // Vsi dnevi fiksnega termina (večdnevni tečaj jih ima več) kot 'YYYY-M-D',
    // da jih koledar lahko označi.
    get fixedLectureDayKeys(): string[] {
        const lecture = this.fixedLecture;
        if (!lecture) return [];
        const stamps = lecture.dateTimes?.length
            ? lecture.dateTimes.map(d => d.ts as number)
            : [lecture.dateTime?.ts as number];
        return stamps
            .filter((ts) => ts !== undefined && ts !== null)
            .map((ts) => {
                const d = new Date(ts as number);
                return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            });
    }

    init() {
        if (this.initialized) return;
        this.initialized = true;

        const fixed = this.fixedLecture;
        if (fixed) {
            this.base.form.step2!.date = fixed;
            this.base.form.step2!.b2bSlug = new URLSearchParams(window.location.search).get('b2b') ?? '';
            this.freePlaces = fixed.freePlaces;
            // Koledar pokažemo v mesecu termina, samo za prikaz datuma - brez nalaganja
            // in brez premikanja po mesecih.
            const start = new Date(fixed.dateTime!.ts as number);
            this.calendarController = new CalendarController(start.getFullYear(), start.getMonth());
            this.selectedDay = start;
            this.calendarReady = true;
            this.reserveSelected();
            return;
        }

        // If no preselected date/lecture, use new combined endpoint for initial load
        if (!this.calendarReady) {
            this.loadFirstAvailableCalendar();
        }

        autorun(() => {
            this.calendarData = undefined;
            return [this.calendarController.month, this.calendarController.year]; // deps
        });
        autorun(() => this.updateCalendarMonth());
        autorun(() => this.updateCalendarDay());

        // Nadaljevanje prijave ali predizbran termin iz povezave: termin je izbran, a
        // rezervacije zanj še nimamo.
        this.reserveSelected();
    }

    /**
     * Rezervira že izbran termin (nadaljevanje prijave, ?lid=, B2B). Če mest ni več,
     * izbiro počisti in uporabnik mora izbrati znova.
     */
    private async reserveSelected() {
        const selected = this.base.form.step2?.date;
        if (!selected?.id || this.base.reservation.active) return;
        if (await this.base.reservation.reserve(selected.id, this.base.childrenMultiplier)) return;
        this.base.form.step2!.date = undefined;
        this.freePlaces = undefined;
        toast.error('Izbrani termin ni več na voljo. Izberite drug termin.');
        this.reloadCalendar();
    }

    /** Ponovno preverjanje razpoložljivosti - po spremembi termina ali števila mest. */
    reloadCalendar() {
        if (this.fixedLecture) return;
        this.calendarReady = true;
        this.updateCalendarMonth();
        this.updateCalendarDay();
    }

    loadFirstAvailableCalendar() {
        if (this.base.form.step1?.children) {
            this.numberOfPeople = this.base.form.step1.children;
        }
        this.api.sendMessage(proto.TxLoadFirstAvailableCalendar.create({
            tecajId: FormStore.meta?.tecaj?.id,
            numberOfPeople: this.numberOfPeople,
            sessionId: this.base.sessionId,
        }));
    }

    dispose() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
    }

    get done(): boolean {
        if (this.fixedLecture) return !this.fixedLectureFull;
        return this.base.config.s2?.allowWithoutDate || this.base.api.authenticated || !!this.base.form.step2?.date;
    };

    updateCalendarDay() {
        // Skip if waiting for initial combined response
        if (!this.calendarReady) return;

        if (!this.selectedDay) {
            this.selectedDayData = undefined;
            return;
        }
        if (this.base.form.step1?.children) {
            this.numberOfPeople = this.base.form.step1.children;
        }
        this.api.sendMessage(proto.TxLoadCalendarDay.create({
            day: new cvv_types.Date({
                year: this.selectedDay.getFullYear(),
                month: this.selectedDay.getMonth() + 1,  // WTF javascript?!
                day: this.selectedDay.getDate(),
            }),
            tecajId: FormStore.meta?.tecaj?.id,
            numberOfPeople: this.numberOfPeople,
            sessionId: this.base.sessionId,
        }));
    }

    updateCalendarMonth() {
        // Skip if waiting for initial combined response
        if (!this.calendarReady) return;

        if (this.base.form.step1?.children) {
            this.numberOfPeople = this.base.form.step1.children;
        }
        this.api.sendMessage(proto.TxLoadCalendar.create({
            month: new cvv_types.Date({
                year: this.calendarController.year,
                month: this.calendarController.month + 1,
            }),
            tecajId: FormStore.meta?.tecaj?.id,
            numberOfPeople: this.numberOfPeople,
            sessionId: this.base.sessionId,
        }));
    }

    setSelectedDay(date?: Date) {
        if (this.selectedDay?.getDate() === date?.getDate()) {
            this.selectedDay = undefined;
        } else {
            this.selectedDay = date;
        }
        this.selectedDayData = undefined;
    }

    async setDate(date: calendar.IAvailableLecture) {
        const reservation = this.base.reservation;
        if (reservation.reserving) return;
        const places = this.base.childrenMultiplier;
        // Ponoven klik na isti termin ne sme podaljšati rezervacije - samo nadaljuje.
        const alreadyHeld = reservation.active
            && reservation.lectureId === date.id
            && reservation.places === places;

        if (!alreadyHeld && !await reservation.reserve(date.id, places)) {
            // Termin je medtem zasedel nekdo drug - izbiro počistimo in osvežimo koledar,
            // da uporabnik vidi dejansko stanje.
            const available = reservation.lastFailure?.placesAvailable ?? 0;
            this.base.form.step2!.date = undefined;
            this.freePlaces = undefined;
            toast.error(places > 1
                ? `Na tem terminu ni več dovolj prostih mest (na voljo: ${available}).`
                : 'Termin ni več na voljo, izberite drug termin.');
            this.reloadCalendar();
            return;
        }

        this.base.form.step2!.date = date;
        this.freePlaces = date.freePlaces;
        this.base.formStore!.meta!.paymentTypes = date.paymentTypes ?? this.base.formStore!.meta!.paymentTypes;
        setTimeout(() => this.base.setStep(this.base.step + 1), 300);
    }

    private onCalendarData(message: proto.RxCalendarData) {
        this.calendarData = message.proto;
        if (this.calendarData?.daysWithLectures?.length && !this.selectedDay) {
            this.setSelectedDay(
                new Date(
                    this.calendarController.year,
                    this.calendarController.month,
                    this.calendarData.daysWithLectures[0]
                )
            );
        }
    }

    private onCalendarDayData(message: proto.RxCalendarDayData) {
        this.selectedDayData = message.proto;

        // Auto-select lecture if lid query param matches (highlight only, don't advance)
        if (this.preselectedLectureId && this.selectedDayData?.lectures) {
            const lecture = this.selectedDayData.lectures.find(l => l.id === this.preselectedLectureId);
            if (lecture) {
                this.base.form.step2!.date = lecture;
                this.freePlaces = lecture.freePlaces;
                this.base.formStore!.meta!.paymentTypes = lecture.paymentTypes ?? this.base.formStore!.meta!.paymentTypes;
                this.preselectedLectureId = undefined; // Clear after use
                this.reserveSelected();
            }
        }
    }

    private onFirstAvailableCalendarData(message: proto.RxFirstAvailableCalendarData) {
        const calendarProto = message.proto.calendar;
        const dayProto = message.proto.day;

        // If no calendar data returned, no termini are available for this course
        if (!calendarProto?.month) {
            this.noTerminiAvailable = true;
            this.calendarReady = true;
            return;
        }

        // Update calendar controller to show the month with first available slot
        runInAction(() => {
            this.calendarController.year = calendarProto.month!.year!;
            this.calendarController.month = calendarProto.month!.month! - 1; // JS months are 0-indexed
        });
        this.calendarData = calendarProto;

        if (dayProto) {
            this.selectedDayData = dayProto;

            // Auto-select the first available day
            if (dayProto.day) {
                this.selectedDay = new Date(
                    dayProto.day.year!,
                    dayProto.day.month! - 1,
                    dayProto.day.day!
                );
            }
        }

        // Mark initial load as done so subsequent navigation uses regular endpoints
        this.calendarReady = true;
    }
}
